import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
  Label,
} from "@asm/ui";
import {
  Plus,
  Trash2,
  Play,
  Pause,
  PlayCircle,
  Calendar,
  Clock,
  GitCompare,
  Check,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  schedulesApi,
  domainsApi,
  type Schedule,
  type Domain,
  type CreateScheduleRequest,
} from "@/lib/api";
import { formatModuleName } from "@/lib/utils";

const AVAILABLE_MODULES = [
  { id: "dns", name: "DNS", description: "DNS records and configuration" },
  { id: "email", name: "Email Security", description: "SPF, DKIM, DMARC analysis" },
  { id: "tls", name: "TLS/SSL", description: "Certificate analysis" },
  { id: "headers", name: "HTTP Headers", description: "Security headers analysis" },
  { id: "whois", name: "WHOIS", description: "Domain registration info" },
  { id: "cloud", name: "Cloud Assets", description: "S3, Azure, GCS bucket discovery" },
  { id: "asn", name: "ASN/BGP", description: "IP ranges and network intelligence" },
  { id: "tech", name: "Technology", description: "Web technology fingerprinting" },
  { id: "repos", name: "Repository Exposure", description: "GitHub/GitLab secret scanning" },
  { id: "crawl_hints", name: "Crawl Hints", description: "robots.txt, sitemap analysis" },
  { id: "js_analysis", name: "JS Analysis", description: "API endpoints, secrets detection" },
  { id: "favicon", name: "Favicon Hash", description: "Application fingerprinting" },
];

const FREQUENCIES = [
  { value: "daily", label: "Daily", interval: 1440 },
  { value: "weekly", label: "Weekly", interval: 10080 },
  { value: "monthly", label: "Monthly", interval: 43200 },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString(),
  label: `${i.toString().padStart(2, "0")}:00`,
}));

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

function formatFrequency(schedule: Schedule): string {
  if (schedule.cron_expression) return "Custom";
  const interval = schedule.interval_minutes;
  if (interval === 60) return "Hourly";
  if (interval === 360) return "Every 6 hours";
  if (interval === 720) return "Every 12 hours";
  if (interval === 1440) return "Daily";
  if (interval === 10080) return "Weekly";
  return "Monthly";
}

function formatNextRun(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  if (diff < 0) return "Overdue";
  if (diff < 3600000) return `In ${Math.round(diff / 60000)} min`;
  if (diff < 86400000) return `In ${Math.round(diff / 3600000)} hours`;
  return date.toLocaleDateString();
}

export default function Schedules() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateScheduleRequest>>({
    frequency: "daily",
    modules: ["dns", "email", "tls", "headers"],
    auto_compare: true,
    preferred_hour: 2,
  });
  const [selectedModules, setSelectedModules] = useState<string[]>([
    "dns", "email", "tls", "headers"
  ]);
  const queryClient = useQueryClient();
  const { t } = useTranslation("schedules");
  const { t: tCommon } = useTranslation("common");

  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ["schedules"],
    queryFn: () => schedulesApi.list(),
  });

  const { data: domains } = useQuery({
    queryKey: ["domains"],
    queryFn: () => domainsApi.list(),
  });

  const { data: tierLimits } = useQuery({
    queryKey: ["tier-limits"],
    queryFn: () => schedulesApi.getTierLimits(),
  });

  const createSchedule = useMutation({
    mutationFn: schedulesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      setDialogOpen(false);
      resetForm();
    },
  });

  const pauseSchedule = useMutation({
    mutationFn: schedulesApi.pause,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const resumeSchedule = useMutation({
    mutationFn: schedulesApi.resume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const triggerSchedule = useMutation({
    mutationFn: schedulesApi.trigger,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const deleteSchedule = useMutation({
    mutationFn: schedulesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
  });

  const resetForm = () => {
    setFormData({
      frequency: "daily",
      modules: ["dns", "email", "tls", "headers"],
      auto_compare: true,
      preferred_hour: 2,
    });
    setSelectedModules(["dns", "email", "tls", "headers"]);
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((m) => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.domain_id || !formData.name || selectedModules.length === 0) return;

    createSchedule.mutate({
      ...formData,
      modules: selectedModules,
    } as CreateScheduleRequest);
  };

  const schedules = schedulesData?.schedules || [];
  const availableDomains = domains || [];

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Calendar className="h-8 w-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {t("title", "Scheduled Scans")}
              </h1>
              <p className="text-muted-foreground">
                {t("description", "Automate recurring security scans for your domains")}
              </p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan">
                <Plus className="mr-2 h-4 w-4" />
                {t("createSchedule", "Create Schedule")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("form.title", "Create New Schedule")}</DialogTitle>
                  <DialogDescription>
                    {t("form.description", "Configure a recurring scan for your domain")}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-6">
                  {createSchedule.error && (
                    <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      {createSchedule.error instanceof Error
                        ? createSchedule.error.message
                        : t("form.failed", "Failed to create schedule")}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="name">{t("form.name", "Schedule Name")}</Label>
                    <Input
                      id="name"
                      placeholder={t("form.namePlaceholder", "e.g., Daily Production Scan")}
                      value={formData.name || ""}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-muted/50 border-border"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t("form.domain", "Domain")}</Label>
                    <Select
                      value={formData.domain_id}
                      onValueChange={(value) => setFormData({ ...formData, domain_id: value })}
                    >
                      <SelectTrigger className="bg-muted/50 border-border">
                        <SelectValue placeholder={t("form.selectDomain", "Select a domain")} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDomains.map((domain: Domain) => (
                          <SelectItem key={domain.id} value={domain.id}>
                            <span className="font-mono">{domain.domain}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("form.frequency", "Frequency")}</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    >
                      <SelectTrigger className="bg-muted/50 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("form.preferredTime", "Preferred Time (UTC)")}</Label>
                      <Select
                        value={formData.preferred_hour?.toString()}
                        onValueChange={(value) => setFormData({ ...formData, preferred_hour: parseInt(value) })}
                      >
                        <SelectTrigger className="bg-muted/50 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HOURS.map((hour) => (
                            <SelectItem key={hour.value} value={hour.value}>
                              {hour.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.frequency === "weekly" && (
                      <div className="space-y-2">
                        <Label>{t("form.preferredDay", "Preferred Day")}</Label>
                        <Select
                          value={formData.preferred_day_of_week?.toString() || "1"}
                          onValueChange={(value) => setFormData({ ...formData, preferred_day_of_week: parseInt(value) })}
                        >
                          <SelectTrigger className="bg-muted/50 border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS_OF_WEEK.map((day) => (
                              <SelectItem key={day.value} value={day.value}>
                                {day.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>{t("form.modules", "Scan Modules")}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_MODULES.map((module) => {
                        const isSelected = selectedModules.includes(module.id);
                        return (
                          <button
                            key={module.id}
                            type="button"
                            onClick={() => toggleModule(module.id)}
                            className={`module-card p-3 rounded-lg text-left ${isSelected ? "selected" : ""}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">{formatModuleName(module.id)}</span>
                              {isSelected && (
                                <CheckCircle2 className="h-4 w-4 text-cyan-400" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {module.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">{t("form.autoCompare", "Auto-Compare")}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t("form.autoCompareDescription", "Automatically compare each scan with the previous execution")}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant={formData.auto_compare ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData({ ...formData, auto_compare: !formData.auto_compare })}
                      className={formData.auto_compare ? "bg-cyan-500 hover:bg-cyan-400 text-black" : ""}
                    >
                      {formData.auto_compare && <Check className="mr-1 h-4 w-4" />}
                      {formData.auto_compare ? "On" : "Off"}
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {tCommon("buttons.cancel", "Cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!formData.domain_id || !formData.name || selectedModules.length === 0 || createSchedule.isPending}
                    className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                  >
                    {createSchedule.isPending
                      ? t("form.creating", "Creating...")
                      : t("form.create", "Create Schedule")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Schedules List */}
      <div className="animate-fade-in-up stagger-1 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-1 rounded-full bg-cyan-500" />
          <div>
            <h2 className="text-lg font-semibold">{t("list.title", "Your Schedules")}</h2>
            <p className="text-xs text-muted-foreground">
              {t("list.description", "Manage your automated security scans")}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : schedules.length ? (
          <div className="space-y-3">
            {schedules.map((schedule: Schedule, index: number) => (
              <div
                key={schedule.id}
                className={`
                  group rounded-lg border border-border bg-muted/30 p-4
                  hover:bg-muted/50 hover:border-cyan-500/30 transition-all
                  animate-fade-in-up stagger-${(index % 8) + 1}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2.5 rounded-lg border ${
                      schedule.enabled
                        ? schedule.consecutive_failures > 0
                          ? "border-amber-500/30 bg-amber-500/10"
                          : "border-emerald-500/30 bg-emerald-500/10"
                        : "border-muted bg-muted/50"
                    }`}>
                      {schedule.enabled ? (
                        schedule.consecutive_failures > 0 ? (
                          <AlertTriangle className="h-5 w-5 text-amber-400" />
                        ) : (
                          <Calendar className="h-5 w-5 text-emerald-400" />
                        )
                      ) : (
                        <Pause className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <Link
                        to={`/schedules/${schedule.id}`}
                        className="font-medium hover:text-cyan-400 transition-colors"
                      >
                        {schedule.name}
                      </Link>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatFrequency(schedule)}
                        </span>
                        <span>{schedule.modules.length} modules</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge
                        variant="outline"
                        className={
                          schedule.enabled
                            ? schedule.consecutive_failures > 0
                              ? "border-amber-500/30 text-amber-400 bg-amber-500/10"
                              : "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                            : "border-muted text-muted-foreground"
                        }
                      >
                        {schedule.enabled
                          ? schedule.consecutive_failures > 0
                            ? `${schedule.consecutive_failures} failures`
                            : "Active"
                          : "Paused"}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Next: {formatNextRun(schedule.next_run_at)}
                      </p>
                    </div>

                    {schedule.auto_compare && schedule.last_comparison_id && (
                      <Link
                        to={`/comparisons?id=${schedule.last_comparison_id}`}
                        className="p-2 rounded-lg border border-border hover:border-cyan-500/30 hover:bg-muted/50 transition-colors"
                        title="View comparison"
                      >
                        <GitCompare className="h-4 w-4 text-cyan-400" />
                      </Link>
                    )}

                    <div className="flex gap-1">
                      {schedule.enabled ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => pauseSchedule.mutate(schedule.id)}
                          disabled={pauseSchedule.isPending}
                          title="Pause"
                          className="h-8 w-8"
                        >
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resumeSchedule.mutate(schedule.id)}
                          disabled={resumeSchedule.isPending}
                          title="Resume"
                          className="h-8 w-8"
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => triggerSchedule.mutate(schedule.id)}
                        disabled={triggerSchedule.isPending}
                        title="Run Now"
                        className="h-8 w-8"
                      >
                        <PlayCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteSchedule.mutate(schedule.id)}
                        disabled={deleteSchedule.isPending}
                        title="Delete"
                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <Link to={`/schedules/${schedule.id}`}>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-cyan-400 transition-colors" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6 inline-block">
              <Calendar className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No scheduled scans yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">
              Create automated schedules to continuously monitor your attack surface.
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("list.createFirst", "Create your first schedule")}
            </Button>
          </div>
        )}
      </div>

      {/* Plan Limits */}
      {tierLimits && (
        <div className="animate-fade-in-up stagger-2 rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-1 rounded-full bg-cyan-500" />
            <h2 className="text-lg font-semibold">{t("limits.title", "Plan Limits")}</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Tier</p>
              <p className="font-medium capitalize">{tierLimits.tier}</p>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Max Schedules</p>
              <p className="font-medium font-mono">
                {tierLimits.max_schedules ?? "Unlimited"}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Min Interval</p>
              <p className="font-medium">
                {tierLimits.min_interval_minutes >= 1440
                  ? `${tierLimits.min_interval_minutes / 1440} day(s)`
                  : `${tierLimits.min_interval_minutes / 60} hour(s)`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
